import { useState } from 'react';
import { FaUpload, FaCheck, FaSpinner, FaUserPlus, FaFileMedical } from 'react-icons/fa';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Variantes de animación para elementos
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07
    }
  }
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 }
};

const DocumentosExtrajudicialForm = ({
  onProcessComplete
}) => {
  const { t } = useTranslation();
  const [hasSecondPerson, setHasSecondPerson] = useState(false);
  const [hasAdditionalDocs, setHasAdditionalDocs] = useState(false);
  
  const [files, setFiles] = useState({
    // Primera persona
    dni_front: null,
    dni_back: null,
    // Segunda persona
    dni_front_2: null,
    dni_back_2: null,
    // Documentos principales
    notary: null,
    registry: null,
    agency: null,
    appraisal: null,
    // Documentos adicionales
    notary_2: null,
    registry_2: null,
    agency_2: null,
    appraisal_2: null
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  // Estado para drag and drop
  const [dragActive, setDragActive] = useState({});
  const [pdfWarning, setPdfWarning] = useState({});

  const documentTypes = {
    // Primera persona
    dni_front: {
      name: t('DNI Frontal - Persona 1'),
      required: true
    },
    dni_back: {
      name: t('DNI Trasero - Persona 1'),
      required: false
    },
    // Segunda persona
    dni_front_2: {
      name: t('DNI Frontal - Persona 2'),
      required: hasSecondPerson,
      condition: hasSecondPerson
    },
    dni_back_2: {
      name: t('DNI Trasero - Persona 2'),
      required: false,
      condition: hasSecondPerson
    },
    // Documentos principales
    notary: {
      name: t('Factura Notaría'),
      required: false
    },
    registry: {
      name: t('Factura Registro'),
      required: false
    },
    agency: {
      name: t('Factura Gestoría'),
      required: false
    },
    appraisal: {
      name: t('Factura Tasación'),
      required: false
    },
    // Documentos adicionales
    notary_2: {
      name: t('Factura Notaría (Adicional)'),
      required: false,
      condition: hasAdditionalDocs
    },
    registry_2: {
      name: t('Factura Registro (Adicional)'),
      required: false,
      condition: hasAdditionalDocs
    },
    agency_2: {
      name: t('Factura Gestoría (Adicional)'),
      required: false,
      condition: hasAdditionalDocs
    },
    appraisal_2: {
      name: t('Factura Tasación (Adicional)'),
      required: false,
      condition: hasAdditionalDocs
    }
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfWarning(prev => ({ ...prev, [type]: true }));
      setFiles(prev => ({ ...prev, [type]: null }));
      return;
    } else {
      setPdfWarning(prev => ({ ...prev, [type]: false }));
    }
    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files.dni_front) {
      setError(t('El DNI frontal de la primera persona es obligatorio'));
      return;
    }

    if (hasSecondPerson && !files.dni_front_2) {
      setError(t('El DNI frontal de la segunda persona es obligatorio'));
      return;
    }

    // Verificar que todos los documentos requeridos estén presentes
    const missingDocs = Object.entries(documentTypes)
      .filter(([key, doc]) => doc.required && !files[key] && (doc.condition === undefined || doc.condition))
      .map(([, doc]) => doc.name);

    if (missingDocs.length > 0) {
      setError(`${t('Los siguientes documentos son obligatorios')}: ${missingDocs.join(', ')}`);
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });
    
    // Añadir información sobre la configuración
    formData.append('hasSecondPerson', hasSecondPerson);
    formData.append('hasAdditionalDocs', hasAdditionalDocs);

    try {
      const supabaseClient = createClient(
        'https://uoclqlfzojdjrhcqhgef.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVvY2xxbGZ6b2pkanJoY3FoZ2VmIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTc3MDEzMDMsImV4cCI6MjAxMzI3NzMwM30.6VY6hK2MWHngYFt3AP9MkV_1hC0xOIuX0CbYIsC5DT0'
      );
      const { data } = await supabaseClient.auth.getSession();
      const access_token = data.session.access_token;

      const response = await axios({
        method: 'post',
        url: 'https://api.office.coventia.es/ai/process_documents',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': access_token
        },
        withCredentials: false
      });

      if (onProcessComplete) {
        onProcessComplete({
          apiResponse: response.data,
          binaryFiles: {
            // Primera persona
            dni_front: files.dni_front,
            dni_back: files.dni_back,
            // Segunda persona
            dni_front_2: files.dni_front_2,
            dni_back_2: files.dni_back_2,
            // Documentos principales
            notary: files.notary,
            registry: files.registry,
            agency: files.agency,
            appraisal: files.appraisal,
            // Documentos adicionales
            notary_2: files.notary_2,
            registry_2: files.registry_2,
            agency_2: files.agency_2,
            appraisal_2: files.appraisal_2
          },
          config: {
            hasSecondPerson,
            hasAdditionalDocs
          }
        });
      }

    } catch (error) {
      console.error('Error al procesar los documentos:', error);
      setError(error.response?.data?.message || t('Error al procesar los documentos', { error }));
    }
    setUploading(false);
  };

  const renderFileInput = (type) => {
    const { name, required, condition } = documentTypes[type];
    
    // No renderizar el input si la condición es falsa
    if (condition === false) return null;
    
    const file = files[type];
    const isDragActive = !!dragActive[type];
    // Handlers de drag and drop
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive((prev) => ({ ...prev, [type]: true }));
    };
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive((prev) => ({ ...prev, [type]: true }));
    };
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive((prev) => ({ ...prev, [type]: false }));
    };
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive((prev) => ({ ...prev, [type]: false }));
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileChange(type, { target: { files: e.dataTransfer.files } });
      }
    };

    return (
      <motion.div 
        className="mb-4 min-w-0"
        initial="hidden"
        animate="visible"
        exit="hidden"
        transition={{ duration: 0.3 }}
      >
        <div className="flex justify-between items-center mb-2 min-w-0">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            {name} {required && <span className="text-rose-500">*</span>}
          </label>
          <AnimatePresence mode="wait">
            {file && (
              <motion.span 
                className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
              >
                <FaCheck className="mr-1" /> {file.name}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
        {pdfWarning[type] && (
          <div className="mb-2 p-2 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs border border-yellow-300 dark:border-yellow-700">
            {t('No se pueden procesar archivos PDF en este campo. Solo se permiten imágenes (.jpg, .jpeg, .png).')}
          </div>
        )}
        <div
          className={`relative ${isDragActive ? 'ring-2 ring-blue-400 border-blue-400 bg-blue-50 dark:ring-blue-500/60 dark:bg-blue-900/30' : ''}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            onChange={(e) => handleFileChange(type, e)}
            className="hidden"
            id={`file-${type}`}
            accept=".jpg,.jpeg,.png"
          />
          <motion.label
            whileHover={{ scale: 1.00 }}
            whileTap={{ scale: 0.99 }}
            htmlFor={`file-${type}`}
            className={
              `flex items-center justify-center w-full h-12 min-h-[30px] px-2 rounded-md border-2 border-dashed dark:bg-gray-900 ` +
              (file
                ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                : isDragActive
                  ? 'border-blue-400 bg-blue-50 dark:border-blue-500/60 dark:bg-blue-900/30'
                  : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500 dark:bg-gray-800 bg-white') +
              ' cursor-pointer transition-colors duration-200'
            }
          >
            <motion.div
              initial={{ rotate: 0 }}
              whileHover={{ rotate: [0, -10, 10, -5, 5, 0] }}
              transition={{ duration: 0.5 }}
            >
              <FaUpload className={`mr-2 ${file
                ? 'text-emerald-500 dark:text-emerald-400'
                : isDragActive
                  ? 'text-blue-500 dark:text-blue-400'
                  : 'text-gray-400 dark:text-gray-500'
                }`} />
            </motion.div>
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[180px]">
              {isDragActive
                ? t('Suelta el archivo aquí')
                : file
                  ? t('Cambiar archivo')
                  : t('Seleccione o arrastre el archivo')}
            </span>
          </motion.label>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-8">
      <AnimatePresence mode="wait">
        <motion.div
          key="form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
        >
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 mt-6 bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700">
            <motion.div 
              className="flex flex-wrap gap-4 mb-6"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              transition={{ staggerChildren: 0.1 }}
            >
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                type="button"
                onClick={() => setHasSecondPerson(!hasSecondPerson)}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 dark:bg-gray-900 ${
                  hasSecondPerson 
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 shadow-sm' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-300'
                }`}
              >
                <motion.div 
                  animate={{ scale: hasSecondPerson ? [1, 1.5, 1] : 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                > 
                  <FaUserPlus className="mr-2" />
                </motion.div>
                {hasSecondPerson ? t('Segunda persona añadida') : t('Añadir segunda persona')}
              </motion.button>
              
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                type="button"
                onClick={() => setHasAdditionalDocs(!hasAdditionalDocs)}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 dark:bg-gray-900 ${
                  hasAdditionalDocs 
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 shadow-sm' 
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-300'
                }`}
              >
                <motion.div 
                  animate={{ scale: hasAdditionalDocs ? [1, 1.5, 1] : 1 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  <FaFileMedical className="mr-2" />
                </motion.div>
                {hasAdditionalDocs ? t('Documentos adicionales añadidos') : t('Añadir documentos adicionales')}
              </motion.button>
            </motion.div>
            
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <motion.h3 
                className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {t('Documentos de identidad')}
              </motion.h3>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {renderFileInput('dni_front')}
                {renderFileInput('dni_back')}
                <AnimatePresence>
                  {hasSecondPerson && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        {renderFileInput('dni_front_2')}
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                      >
                        {renderFileInput('dni_back_2')}
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <motion.h3 
                className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {t('Documentos de facturas')}
              </motion.h3>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {renderFileInput('notary')}
                {renderFileInput('registry')}
                {renderFileInput('agency')}
                {renderFileInput('appraisal')}
              </motion.div>
            </motion.div>
            
            <AnimatePresence>
              {hasAdditionalDocs && (
                <motion.div 
                  className="mb-6"
                  initial={{ opacity: 0, height: 0, overflow: "hidden" }}
                  animate={{ opacity: 1, height: "auto", overflow: "visible" }}
                  exit={{ opacity: 0, height: 0, overflow: "hidden" }}
                  transition={{ duration: 0.5 }}
                >
                  <motion.h3 
                    className="text-lg font-semibold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {t('Documentos adicionales')}
                  </motion.h3>
                  <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                  >
                    {renderFileInput('notary_2')}
                    {renderFileInput('registry_2')}
                    {renderFileInput('agency_2')}
                    {renderFileInput('appraisal_2')}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {error && (
                <motion.div 
                  className="mb-4 p-3 rounded bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 text-sm border border-rose-200 dark:border-rose-700"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              type="submit"
              disabled={uploading}
              className={`
                w-full py-2 px-4 rounded-md text-white font-medium dark:bg-primary-600
                ${uploading
                  ? 'bg-gray-400 dark:bg-gray-700 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-700'
                }
                transition-colors duration-200
              `}
              whileHover={!uploading ? { scale: 1.02 } : {}}
              whileTap={!uploading ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              {uploading ? (
                <motion.span 
                  className="flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <div>
                    <FaSpinner className="mr-2 animate-spin" />
                  </div>
                  {t('Procesando documentos...')}
                </motion.span>
              ) : (
                t('Procesar Documentos')
              )}
            </motion.button>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

DocumentosExtrajudicialForm.propTypes = {
  onProcessComplete: PropTypes.func.isRequired
};

export default DocumentosExtrajudicialForm;
